import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { API_BASE_URL, getDispatcherToken } from './DispatcherApp';
import EditScheduleModal from './EditScheduleModal';
import ExtraScheduleReceipt from '../../components/fleet/ExtraScheduleReceipt';
import { addDaysToDateKey, getTokyoDateKey } from '../../utils/tokyoDate';
import { GanttChart, List, Moon } from 'lucide-react';
import DriverDayTimeline from './DriverDayTimeline';
import { cancelConfirmation, showOperationFeedback } from './scheduleOperation';
import { scheduleOwner } from './scheduleOwner';

export default function DriverScheduleModal({ driver, drivers, dispatcher, onClose, onAddSchedule, onRefresh }) {
  const [schedules, setSchedules] = useState({ official_orders: [], extra_schedules: [] });
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState('all');
  const [includeCancelled, setIncludeCancelled] = useState(false);
  const [includePast, setIncludePast] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState('list');
  const cancelBusy = useRef(false);
  const [cancelling, setCancelling] = useState(false);

  const today = getTokyoDateKey();

  // 计算日期范围
  const dateParams = useMemo(() => {
    let dateFrom, dateTo;
    
    switch (timeRange) {
      case 'today':
        dateFrom = today;
        dateTo = today;
        break;
      case 'tomorrow':
        dateFrom = addDaysToDateKey(today, 1);
        dateTo = dateFrom;
        break;
      case '7days':
        dateFrom = includePast ? null : today;
        dateTo = addDaysToDateKey(today, 7);
        break;
      case '30days':
        dateFrom = includePast ? null : today;
        dateTo = addDaysToDateKey(today, 30);
        break;
      case 'all':
      default:
        dateFrom = includePast ? null : today;
        dateTo = null;
        break;
    }
    
    // 限定天数视图保留近期历史；全部视图不限制历史起点。
    if (includePast && !dateFrom && timeRange !== 'all') {
      dateFrom = addDaysToDateKey(today, -30);
    }
    
    return { dateFrom, dateTo };
  }, [timeRange, includePast, today]);

  const fetchSchedules = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (dateParams.dateFrom) params.append('date_from', dateParams.dateFrom);
      if (dateParams.dateTo) params.append('date_to', dateParams.dateTo);
      params.append('include_cancelled', includeCancelled.toString());
      
      const response = await fetch(
        `${API_BASE_URL}/dispatcher/driver/${driver.id}/schedules?${params}`,
        { headers: { 'Authorization': `Bearer ${getDispatcherToken()}` } }
      );
      if (response.ok) {
        setSchedules(await response.json());
      }
    } catch (err) {
      console.error('Failed to fetch schedules:', err);
    } finally {
      setLoading(false);
    }
  }, [dateParams, driver.id, includeCancelled]);

  useEffect(() => {
    fetchSchedules();
  }, [fetchSchedules]);

  const handleCancelSchedule = async (schedule) => {
    if (cancelBusy.current || !canCancel(schedule) || !window.confirm(cancelConfirmation(schedule))) return;
    cancelBusy.current = true;
    setCancelling(true);
    
    try {
      const params = new URLSearchParams({ expected_revision: String(schedule.revision ?? 0) });
      if (schedule.updated_at) params.set('expected_updated_at', schedule.updated_at);
      const response = await fetch(`${API_BASE_URL}/dispatcher/extra-schedule/${schedule.id}/cancel?${params}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${getDispatcherToken()}` }
      });
      if (response.ok) {
        showOperationFeedback(await response.json());
        await fetchSchedules();
        onRefresh?.();
      } else {
        const data = await response.json();
        if (response.status === 409) await fetchSchedules();
        alert(data.detail || '取消失败');
      }
    } catch (err) {
      alert('取消失败');
    } finally {
      cancelBusy.current = false;
      setCancelling(false);
    }
  };

  // 合并并按日期分组排序所有排班
  const groupedSchedules = useMemo(() => {
    const restItems = (schedules.time_off || []).flatMap(item => {
      if (['deleted', 'cancelled', 'canceled', '已取消'].includes(item.status)) return [];
      const start = item.start_date;
      const end = item.end_date || start;
      if (![start, end].every(date => /^\d{4}-\d{2}-\d{2}$/.test(date || '') && addDaysToDateKey(date, 0) === date)) return [];
      const first = dateParams.dateFrom && dateParams.dateFrom > start ? dateParams.dateFrom : start;
      const last = dateParams.dateTo && dateParams.dateTo < end ? dateParams.dateTo : end;
      const days = [];
      for (let date = first; date <= last; date = addDaysToDateKey(date, 1)) {
        days.push({ ...item, date, time: item.all_day !== false ? '00:00' : item.start_time,
          _type: 'rest', timeline_type: 'rest',
          notes: item.all_day !== false ? '全天休息' : `${item.start_time || '--:--'}-${item.end_time || '--:--'} 休息` });
      }
      return days;
    });
    let allItems = [
      ...schedules.official_orders.map(o => ({ ...o, _type: 'official' })),
      ...schedules.extra_schedules.map(e => ({ ...e, _type: 'extra' })),
      ...restItems
    ];
    
    // Local search filter
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      allItems = allItems.filter(item =>
        (item.pickup_location || '').toLowerCase().includes(q) ||
        (item.dropoff_location || '').toLowerCase().includes(q) ||
        (item.customer_name || '').toLowerCase().includes(q) ||
        (item.customer_company || '').toLowerCase().includes(q) ||
        (item.flight_number || '').toLowerCase().includes(q) ||
        (item.notes || '').toLowerCase().includes(q) ||
        (item.order_no || '').toLowerCase().includes(q) ||
        (item.customer_code || '').toLowerCase().includes(q) ||
        (item.id || '').toLowerCase().includes(q)
      );
    }
    
    // 按日期时间排序
    allItems.sort((a, b) => {
      const dateA = `${a.date} ${a.time || '00:00'}`;
      const dateB = `${b.date} ${b.time || '00:00'}`;
      return dateA.localeCompare(dateB);
    });
    
    // 按日期分组
    const groups = {};
    allItems.forEach(item => {
      const date = item.date || 'unknown';
      if (!groups[date]) {
        groups[date] = [];
      }
      groups[date].push(item);
    });
    
    // 转换为数组并排序
    return Object.entries(groups)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([date, items]) => ({ date, items }));
  }, [schedules, searchQuery, dateParams]);

  // 统计数量
  const stats = useMemo(() => {
    const official = schedules.official_orders.filter(o => 
      o.status !== 'cancelled' && o.status !== '已取消'
    ).length;
    const driverExtra = schedules.extra_schedules.filter(e => 
      e.source_type === 'driver_extra' && e.status === 'active'
    ).length;
    const dispatcherExtra = schedules.extra_schedules.filter(e => 
      e.source_type === 'dispatcher_extra' && e.status === 'active'
    ).length;
    return { official, driverExtra, dispatcherExtra, total: official + driverExtra + dispatcherExtra };
  }, [schedules]);

  // 来源标签
  const getSourceLabel = (item) => {
    if (item._type === 'official' || item.source_type === 'official') {
      return { text: '正式', color: 'premium-chip-success' };
    }
    if (item.source_type === 'dispatcher_extra') {
      const name = item.created_by_name || '调度';
      return { text: `调度追加（${name}）`, color: 'premium-chip-warning' };
    }
    const name = item.created_by_name || '司机';
    return { text: `司机追加（${name}）`, color: 'premium-chip' };
  };

  // 订单类型标签
  const getServiceTypeLabel = (item) => {
    const t = item.service_type;
    if (t === 'charter') return { text: '包车', color: 'premium-chip-success' };
    if (t === 'other') return { text: '其他', color: 'premium-chip' };
    return { text: '接送', color: 'premium-chip' };
  };

  // 状态标签
  const getStatusLabel = (item) => {
    const itemDate = item.date;
    const status = item.status;
    
    if (status === 'cancelled' || status === '已取消') {
      return { text: '已取消', color: 'premium-chip-destructive' };
    }
    if (status === 'completed' || status === '已完成') {
      return { text: '已完成', color: 'premium-chip' };
    }
    if (itemDate === today) {
      return { text: '今天', color: 'premium-chip-warning' };
    }
    if (itemDate > today) {
      return { text: '将来', color: 'premium-chip-success' };
    }
    return { text: '过去', color: 'premium-chip' };
  };

  // 日期标签
  const formatDateHeader = (dateStr) => {
    const date = new Date(dateStr);
    const weekdays = ['周日', '周一', '周二', '周三', '周四', '周五', '周六'];
    const weekday = weekdays[date.getDay()];
    
    if (dateStr === today) {
      return `${dateStr} (${weekday}) - 今天`;
    }
    
    if (dateStr === addDaysToDateKey(today, 1)) {
      return `${dateStr} (${weekday}) - 明天`;
    }
    
    return `${dateStr} (${weekday})`;
  };

  const canCancel = (item) => {
    if (item.created_by_role === 'admin') return false;
    // 1. 必须有取消权限
    if (!dispatcher?.permissions?.can_cancel) return false;
    // 2. 正式订单不能取消
    if (item._type === 'official') return false;
    // 3. 状态必须是 active
    if (item.status !== 'active') return false;
    // 4. 只能取消自己创建的调度追加
    if (item.source_type !== 'dispatcher_extra') return false;
    return item.created_by_id === dispatcher?.id;
  };

  // 权限检查
  const canViewPrice = dispatcher?.permissions?.can_view_price;
  const canViewCustomer = dispatcher?.permissions?.can_view_customer_info;
  const canCreate = dispatcher?.permissions?.can_create;

  return (
    <div className="fixed inset-0 bg-black/75 flex items-center justify-center z-50 p-3 sm:p-4">
      <div className="dispatcher-schedule-modal bg-card text-card-foreground rounded-xl shadow-2xl w-full max-w-4xl h-[92dvh] flex flex-col overflow-hidden border border-border-subtle">
        {/* Header */}
        <div className="p-4 border-b border-border-subtle flex justify-between items-center bg-surface rounded-t-lg">
          <div>
            <h2 className="text-base font-semibold text-foreground">
              {driver.name} 的排班详情
            </h2>
            <p className="text-xs text-muted-foreground mt-0.5">{driver.phone}</p>
          </div>
          <button
            onClick={onClose}
            className="w-7 h-7 flex items-center justify-center rounded hover:bg-muted transition"
          >
            <svg className="w-5 h-5 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Stats & Filters */}
        <div className="p-3 border-b border-border-subtle bg-card space-y-3">
          {/* Stats */}
          <div className="flex items-center gap-5 text-xs">
            <span className="flex items-center gap-1.5">
              <span className="w-2 h-2 rounded bg-primary"></span>
              <span className="text-muted-foreground">正式: <span className="font-medium text-foreground">{stats.official}</span></span>
            </span>
            <span className="flex items-center gap-1.5">
              <span className="w-2 h-2 rounded bg-warning"></span>
              <span className="text-muted-foreground">司机追加: <span className="font-medium text-foreground">{stats.driverExtra}</span></span>
            </span>
            <span className="flex items-center gap-1.5">
              <span className="w-2 h-2 rounded bg-success"></span>
              <span className="text-muted-foreground">调度追加: <span className="font-medium text-foreground">{stats.dispatcherExtra}</span></span>
            </span>
            <span className="text-muted-foreground">|</span>
            <span className="text-muted-foreground">共 <span className="font-medium">{stats.total}</span> 个排班</span>
          </div>
          
          {/* Filters */}
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-2">
              <span className="text-xs text-muted-foreground">时间范围:</span>
              <div className="flex gap-1 flex-wrap">
                {[
                  { key: 'today', label: '今天' },
                  { key: 'tomorrow', label: '明天' },
                  { key: '7days', label: '7天' },
                  { key: '30days', label: '30天' },
                  { key: 'all', label: '全部' }
                ].map(opt => (
                  <button
                    key={opt.key}
                    onClick={() => setTimeRange(opt.key)}
                    className={`px-2 py-1 text-xs rounded transition ${
                      timeRange === opt.key
                        ? 'bg-primary text-white'
                        : 'bg-surface text-muted-foreground hover:bg-muted'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="flex items-center gap-4">
              <label className="flex items-center gap-1.5 cursor-pointer">
                <input
                  type="checkbox"
                  checked={includePast}
                  onChange={(e) => setIncludePast(e.target.checked)}
                  className="w-3.5 h-3.5 text-muted-foreground rounded"
                />
                <span className="text-xs text-muted-foreground">显示过去</span>
              </label>
              <label className="flex items-center gap-1.5 cursor-pointer">
                <input
                  type="checkbox"
                  checked={includeCancelled}
                  onChange={(e) => setIncludeCancelled(e.target.checked)}
                  className="w-3.5 h-3.5 text-muted-foreground rounded"
                />
                <span className="text-xs text-muted-foreground">显示已取消</span>
              </label>
            </div>
          </div>
          
          <div className="inline-flex rounded-md border border-border-subtle p-1" role="group" aria-label="排班显示方式">
            {[['list', '列表', List], ['chart', '图表', GanttChart]].map(([value, label, Icon]) => (
              <button key={value} type="button" aria-pressed={viewMode === value} onClick={() => setViewMode(value)}
                className={`inline-flex items-center gap-1.5 rounded px-3 py-1.5 text-xs font-semibold ${viewMode === value ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-surface'}`}>
                <Icon className="h-4 w-4" />{label}
              </button>
            ))}
          </div>
          {/* Search */}
          <div>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="搜索: 客人姓名/航班号/地点/公司..."
              className="w-full px-3 py-1.5 border border-border-subtle rounded-lg text-xs focus:ring-1 focus:ring-ring focus:outline-none bg-card"
              data-testid="schedule-search"
            />
          </div>
        </div>

        {/* Schedule List - Grouped by Date */}
        <div className="dispatcher-schedule-list min-h-0 flex-1 overflow-y-auto overscroll-contain bg-card">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-6 w-6 border-2 border-input border-t-primary"></div>
              <span className="ml-3 text-muted-foreground text-sm">加载中...</span>
            </div>
          ) : groupedSchedules.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <svg className="w-12 h-12 mx-auto mb-3 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              <p className="text-sm">暂无排班记录</p>
              <p className="text-xs text-muted-foreground mt-1">当前筛选条件下没有找到排班</p>
            </div>
          ) : viewMode === 'chart' ? (
            <div className="space-y-3 p-3" data-testid="schedule-chart">
              {groupedSchedules.map(({ date, items }) => (
                <DriverDayTimeline key={date} day={{ date, label: date === 'unknown' ? '日期待确认' : date, weekday: date === 'unknown' ? '-' : ['日', '一', '二', '三', '四', '五', '六'][new Date(`${date}T00:00:00`).getDay()], items: items.map(item => ({ ...item, timeline_type: item._type === 'rest' ? 'rest' : item._type === 'official' ? 'official' : item.source_type })) }}
                  onSelectTimelineItem={setEditingItem} />
              ))}
            </div>
          ) : (
            <div>
              {groupedSchedules.map(({ date, items }) => (
                <div key={date} data-schedule-date={date}>
                  {/* Date Header */}
                  <div className={`sticky top-0 px-4 py-2 text-xs font-medium border-b border-border-subtle ${
                    date === today
                      ? 'bg-warning/15 text-warning-foreground border-warning/35'
                        : date < today
                        ? 'bg-surface text-muted-foreground border-border-subtle'
                        : 'bg-primary/10 text-primary-foreground border-primary/25'
                  }`}>
                    {formatDateHeader(date)}
                    <span className="ml-2 text-muted-foreground font-normal">({items.length}个)</span>
                  </div>
                  
                  {/* Items for this date */}
                  <div className="divide-y divide-border-subtle">
                    {items.map((item, index) => {
                      if (item._type === 'rest') return (
                        <div key={`rest-${item.id || index}`} data-testid="schedule-rest-item"
                          className="flex items-center gap-2 border-l-2 border-rose-300/50 px-3 py-3 text-sm text-rose-200">
                          <Moon className="h-4 w-4 shrink-0" aria-hidden="true" />
                          <span className="min-w-0 break-words">{item.notes}</span>
                        </div>
                      );
                      const sourceLabel = getSourceLabel(item);
                      const statusLabel = getStatusLabel(item);
                      const isCancelled = item.status === 'cancelled' || item.status === '已取消';
                      const isPast = item.date < today;

                      return (
                        <div
                          key={`${item._type}-${item.id || index}`}
                          data-testid={`schedule-item-${item.id || index}`}
                          style={{ '--dispatch-owner-color': scheduleOwner(item).color }}
                          className={`dispatch-owner-accent p-3 hover:bg-surface cursor-pointer transition ${
                            isCancelled ? 'bg-destructive/10 opacity-70' :
                            isPast ? 'bg-surface/50 opacity-70' : ''
                          }`}
                          onClick={() => setEditingItem(item)}
                        >
                          <div className="flex justify-between items-start">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-1.5 mb-1.5 flex-wrap">
                                <span className="font-medium text-foreground text-sm min-w-[50px]">
                                  {item.time || '--:--'}
                                </span>
                                <span style={{ color: scheduleOwner(item).color, border: '1px solid currentColor' }} className="px-1.5 py-0.5 rounded text-xs">
                                  {sourceLabel.text}
                                </span>
                                <span className={`px-1.5 py-0.5 rounded text-xs ${getServiceTypeLabel(item).color}`}>
                                  {getServiceTypeLabel(item).text}
                                </span>
                                <span className={`px-1.5 py-0.5 rounded text-xs ${statusLabel.color}`}>
                                  {statusLabel.text}
                                </span>
                                {canViewPrice && <ExtraScheduleReceipt schedule={item} />}
                                {canViewPrice && item.price_amount != null && (
                                  <span className="px-1.5 py-0.5 rounded text-xs bg-surface text-muted-foreground">
                                    {item.price_currency === 'CNY' ? '¥' : '¥'}{item.price_amount?.toLocaleString()} {item.price_currency}
                                  </span>
                                )}
                              </div>

                              <div className="text-xs text-muted-foreground ml-[58px]" style={{overflowWrap:'anywhere'}}>
                                <div className="flex items-center gap-1.5 mb-0.5">
                                  <span className="text-success text-xs flex-shrink-0">●</span>
                                  <span>{item.pickup_location || '-'}</span>
                                </div>
                                <div className="flex items-center gap-1.5">
                                  <span className="text-destructive text-xs flex-shrink-0">●</span>
                                  <span>{item.dropoff_location || '-'}</span>
                                </div>
                              </div>

                              {/* Extra info row */}
                              <div className="flex items-center gap-3 mt-1 ml-[58px] text-xs text-muted-foreground flex-wrap">
                                {item.flight_number && <span>✈ {item.flight_number}</span>}
                                {item.order_no && <span>订单号 {item.order_no}</span>}
                                {canViewCustomer && item.customer_code && <span>#{item.customer_code}</span>}
                                {canViewCustomer && item.customer_name && <span>👤 {item.customer_name}</span>}
                                {canViewCustomer && item.customer_company && <span>🏢 {item.customer_company}</span>}
                                {item.passenger_count && <span>{item.passenger_count}人</span>}
                                {item.luggage_count && <span>{item.luggage_count}件行李</span>}
                                {item.vehicle_type && <span>🚗 {item.vehicle_type}</span>}
                              </div>

                              {item.notes && (
                                <p className="text-xs text-muted-foreground mt-1 ml-[58px] truncate">备注: {item.notes}</p>
                              )}
                              {item.internal_notes && (
                                <p className="text-xs premium-chip-warning mt-0.5 ml-[58px]">{item.internal_notes ? `内部备注: ${item.internal_notes}` : ''}</p>
                              )}
                            </div>

                            <div className="flex items-center gap-1 flex-shrink-0 ml-2">
                              {canCancel(item) && (
                                <button
                                  disabled={cancelling}
                                  onClick={(e) => { e.stopPropagation(); handleCancelSchedule(item); }}
                                  className="px-2 py-1 bg-destructive text-destructive-foreground rounded-lg text-xs hover:bg-destructive/85 transition"
                                >
                                  取消
                                </button>
                              )}
                              <svg className="w-4 h-4 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                              </svg>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-3 border-t border-border-subtle bg-surface flex justify-between rounded-b-lg">
          {canCreate ? (
            <button
              onClick={onAddSchedule}
              className="px-3 py-1.5 bg-primary text-white rounded text-xs hover:bg-muted transition flex items-center gap-1.5"
            >
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              添加排班
            </button>
          ) : (
            <div></div>
          )}
            <button
              onClick={onClose}
              className="px-4 py-1.5 bg-muted text-foreground rounded-lg text-xs hover:bg-surface transition"
            >
            关闭
          </button>
        </div>
      </div>

      {/* Edit Modal */}
      {editingItem && (
        <EditScheduleModal
          schedule={editingItem}
          drivers={drivers}
          dispatcher={dispatcher}
          onClose={() => setEditingItem(null)}
          onSaved={() => {
            setEditingItem(null);
            fetchSchedules();
            onRefresh?.();
          }}
        />
      )}
    </div>
  );
}
